/**
 * datevBelege — reine, deterministische Beleg-Verknüpfungslogik für den DATEV-Belege-Export (M7b).
 *
 * Verantwortung: stabiler Beleg-GUID (UUIDv5), DATEV-Beleglink-Zellwert und fail-closed-
 * Zuordnung von Beleglinks zu Buchungssätzen. Die `document.xml` (DATEV-Belegindex) wird
 * seit M7b-5c ausschließlich serverseitig gebaut (s. Function `buildServerDocumentXml`).
 *
 * Leitprinzipien:
 *   - VOLLSTÄNDIG PUR: keine Firebase-/React-Imports, KEIN `Math.random`, KEIN `crypto`-Random.
 *     Der GUID ist eine deterministische UUIDv5 über einen festen Namespace — derselbe
 *     `documentKey` ergibt IMMER denselben GUID (idempotente Re-Exporte, FE↔Function-Identität).
 *   - FAIL-CLOSED: ein Beleglink entsteht NUR, wenn tatsächlich eine Datei vorliegt
 *     (`assignBeleglinks`) — nie ein toter DATEV-Verweis.
 *   - Von `datevExport.ts` werden ausschließlich TYPEN importiert (`import type`, zur Laufzeit
 *     gelöscht) → kein Laufzeit-Zyklus, obwohl `datevExport.ts` `deriveBelegGuid` (Wert) importiert.
 *
 * ⚠️ GUID-VERTRAG (HART GEPINNT): Namespace, Name-String (`documentKey`), UUIDv5-Algorithmus
 * und Uppercase-Ausgabe MÜSSEN bit-identisch zur Firebase-Function (M7b-2) sein — der Server
 * recomputet den GUID und verwirft bei Mismatch. Änderungen hier brechen die Server-Autorisierung.
 *
 * @module datevBelege
 */

import type { DatevBookingRow } from './datevExport';

// ─── GUID-Vertrag (SoT — bit-identisch zur Function M7b-2 halten) ─────────────────

/**
 * Fixer Namespace-UUID für die deterministische Beleg-GUID-Ableitung (UUIDv5).
 * NICHT ändern — der Server nutzt exakt diese Konstante zum Recompute.
 */
export const DATEV_BELEG_GUID_NAMESPACE = 'a7f3e2d1-6b4c-4e8a-9f2d-7c1b5e9a3d84';

// ─── Reine UUIDv5-Implementierung (RFC 4122, SHA-1) ───────────────────────────────
//
// Bewusst inline statt `uuid`-Dependency: `uuid` ist im `frontend-migrated`-Package NICHT
// als direkte Abhängigkeit deklariert; eine neue Dependency ohne Not wäre gegen die Vorgabe.
// Die Implementierung ist pur (arbeitet auf Byte-Arrays) und im Test gegen Node-`crypto`
// verifiziert.

/** UTF-8-Kodierung eines Strings zu einem Byte-Array (pur, ohne TextEncoder-Abhängigkeit). */
function utf8Bytes(input: string): number[] {
  const bytes: number[] = [];
  for (let i = 0; i < input.length; i += 1) {
    let code = input.charCodeAt(i);
    // Surrogate-Paare zu vollem Codepoint zusammenführen.
    if (code >= 0xd800 && code <= 0xdbff && i + 1 < input.length) {
      const next = input.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        code = 0x10000 + ((code - 0xd800) << 10) + (next - 0xdc00);
        i += 1;
      }
    }
    if (code < 0x80) {
      bytes.push(code);
    } else if (code < 0x800) {
      bytes.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
    } else if (code < 0x10000) {
      bytes.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
    } else {
      bytes.push(
        0xf0 | (code >> 18),
        0x80 | ((code >> 12) & 0x3f),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f),
      );
    }
  }
  return bytes;
}

/** 32-Bit-Linksrotation (SHA-1-Kern). */
function rotl32(value: number, shift: number): number {
  return ((value << shift) | (value >>> (32 - shift))) >>> 0;
}

/**
 * Reine SHA-1-Implementierung. Eingabe: Byte-Array. Ausgabe: 20-Byte-Digest als Byte-Array.
 * RFC 3174 — deterministisch, kein externer Zustand.
 */
function sha1Bytes(message: number[]): number[] {
  const ml = message.length * 8; // Nachrichtenlänge in Bit
  const padded = message.slice();
  padded.push(0x80);
  while (padded.length % 64 !== 56) {
    padded.push(0x00);
  }
  // 64-Bit-Big-Endian-Länge anhängen (obere 32 Bit sind hier stets 0 — Länge < 2^32 Byte).
  const hi = Math.floor(ml / 0x100000000);
  const lo = ml >>> 0;
  padded.push((hi >>> 24) & 0xff, (hi >>> 16) & 0xff, (hi >>> 8) & 0xff, hi & 0xff);
  padded.push((lo >>> 24) & 0xff, (lo >>> 16) & 0xff, (lo >>> 8) & 0xff, lo & 0xff);

  let h0 = 0x67452301;
  let h1 = 0xefcdab89;
  let h2 = 0x98badcfe;
  let h3 = 0x10325476;
  let h4 = 0xc3d2e1f0;

  const w = new Array<number>(80);
  for (let chunk = 0; chunk < padded.length; chunk += 64) {
    for (let i = 0; i < 16; i += 1) {
      const j = chunk + i * 4;
      w[i] =
        ((padded[j] << 24) | (padded[j + 1] << 16) | (padded[j + 2] << 8) | padded[j + 3]) >>> 0;
    }
    for (let i = 16; i < 80; i += 1) {
      w[i] = rotl32(w[i - 3] ^ w[i - 8] ^ w[i - 14] ^ w[i - 16], 1);
    }

    let a = h0;
    let b = h1;
    let c = h2;
    let d = h3;
    let e = h4;

    for (let i = 0; i < 80; i += 1) {
      let f: number;
      let k: number;
      if (i < 20) {
        f = (b & c) | (~b & d);
        k = 0x5a827999;
      } else if (i < 40) {
        f = b ^ c ^ d;
        k = 0x6ed9eba1;
      } else if (i < 60) {
        f = (b & c) | (b & d) | (c & d);
        k = 0x8f1bbcdc;
      } else {
        f = b ^ c ^ d;
        k = 0xca62c1d6;
      }
      const temp = (rotl32(a, 5) + f + e + k + w[i]) >>> 0;
      e = d;
      d = c;
      c = rotl32(b, 30);
      b = a;
      a = temp;
    }

    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
  }

  const out: number[] = [];
  for (const h of [h0, h1, h2, h3, h4]) {
    out.push((h >>> 24) & 0xff, (h >>> 16) & 0xff, (h >>> 8) & 0xff, h & 0xff);
  }
  return out;
}

/** Wandelt eine hyphenierte UUID-Zeichenkette in 16 Bytes. */
function uuidToBytes(uuid: string): number[] {
  const hex = uuid.replace(/-/g, '');
  const bytes: number[] = [];
  for (let i = 0; i < 16; i += 1) {
    bytes.push(parseInt(hex.substr(i * 2, 2), 16));
  }
  return bytes;
}

/** Formatiert 16 Bytes als hyphenierte UUID (Uppercase). */
function bytesToUuid(bytes: number[]): string {
  const hex = bytes.map((b) => b.toString(16).padStart(2, '0')).join('');
  return (
    `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-` +
    `${hex.slice(16, 20)}-${hex.slice(20, 32)}`
  ).toUpperCase();
}

/** Reine UUIDv5-Berechnung (SHA-1) über Namespace + Name. */
function uuidV5(name: string, namespaceUuid: string): string {
  const message = uuidToBytes(namespaceUuid).concat(utf8Bytes(name));
  const hash = sha1Bytes(message);
  const bytes = hash.slice(0, 16);
  bytes[6] = (bytes[6] & 0x0f) | 0x50; // Version 5
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // RFC-4122-Variante
  return bytesToUuid(bytes);
}

// ─── Öffentliche Beleg-API (rein) ─────────────────────────────────────────────────

/**
 * Deterministischer, stabiler Beleg-GUID je `documentKey` (UUIDv5 über
 * {@link DATEV_BELEG_GUID_NAMESPACE}), Ausgabe in GROSSBUCHSTABEN (DATEV-Konvention).
 *
 * GUID-VERTRAG: Name-String ist der `documentKey` exakt (`${collection}:${docId}`,
 * collection kleingeschrieben, `:`-Trenner, KEIN Trim/Whitespace). Muss bit-identisch
 * zur Function M7b-2 bleiben.
 *
 * TEST-VEKTOR (FE↔Function-Identitätsprüfung):
 *   deriveBelegGuid('invoices:test-doc-1') === '3328C8C8-F847-57ED-A888-B8353F2FA953'
 *
 * @param documentKey Stabiler Schlüssel `${collection}:${docId}`.
 * @returns UUIDv5 als hyphenierte UUID in Uppercase.
 */
export function deriveBelegGuid(documentKey: string): string {
  return uuidV5(documentKey, DATEV_BELEG_GUID_NAMESPACE);
}

/**
 * Baut den DATEV-Beleglink-Zellwert aus einem GUID: `BEDI "<GUID>"` (Präfix `BEDI`,
 * ein Leerzeichen, GUID in einfachen `"`-Anführungszeichen).
 *
 * WICHTIG: Die für die CSV nötige Verdopplung der inneren `"` übernimmt `quoteText`
 * beim CSV-Bau in `datevExport.ts` — hier NICHT vor-verdoppeln.
 *
 * 🔴 [E-RECHNUNG-GATE M7b-4]: exakte Syntax (`BEDI`-Präfix, Quoting) gegen echte
 * EXTF-Formatversion-13-Referenz bestätigen.
 *
 * @param guid Beleg-GUID (Uppercase, aus {@link deriveBelegGuid}).
 * @returns Beleglink-Zellwert für Spalte 20.
 */
export function formatBeleglink(guid: string): string {
  return `BEDI "${guid}"`;
}

/**
 * Setzt `beleglink` NUR für Rows, deren `documentKey` in `availableKeys` enthalten ist
 * (= Datei liegt real vor) — fail-closed: alle anderen Rows behalten ihren leeren
 * Beleglink, es entsteht nie ein toter Link. REIN: liefert neue Array-/Objekt-Instanzen,
 * mutiert die Eingabe nicht.
 *
 * @param rows Buchungssätze (ggf. mit `documentKey`).
 * @param availableKeys Menge der `documentKey`s, für die eine Datei bestätigt ist.
 * @returns Neue Row-Liste mit gesetztem `beleglink`, wo eine Datei vorliegt.
 */
export function assignBeleglinks(
  rows: DatevBookingRow[],
  availableKeys: ReadonlySet<string>,
): DatevBookingRow[] {
  return (rows ?? []).map((row) => {
    if (row.documentKey && availableKeys.has(row.documentKey)) {
      return { ...row, beleglink: formatBeleglink(deriveBelegGuid(row.documentKey)) };
    }
    return { ...row };
  });
}

// ─── document.xml (DATEV-Belegindex) ──────────────────────────────────────────────
//
// M7b-5c/P3: Die `document.xml` wird ausschließlich SERVERSEITIG gebaut
// (`functions/src/datev/buildDatevBelegePaket.ts::buildServerDocumentXml`, DATEV-Belegtransfer-
// konforme Struktur mit `<content>`-Wrapper + `<header>`-Ordnungsbegriff + `File`-Attribut).
// Der frühere FE-Builder `buildDatevDocumentXml` (divergentes, nicht-schemakonformes Schema)
// wurde entfernt — der Server ist alleinige XML-Autorität (kein opakes FE-XML mehr).
